import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Search, ChevronLeft, ChevronRight, CheckCircle, XCircle, X, Building2, Plus } from "lucide-react";
import { db } from "../firebase";
import { collection, getDocs, doc, updateDoc, getDoc, setDoc, addDoc, serverTimestamp, deleteDoc } from "firebase/firestore";
import { getDepartments } from "../utils/getDepartments";

export default function DevfolioUserAccess() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [updatingDepartment, setUpdatingDepartment] = useState(null); // Track which department is being updated
  const [userDepartments, setUserDepartments] = useState({}); // Map email -> department
  const [selectedDepartment, setSelectedDepartment] = useState(""); // Department filter
  const [departments, setDepartments] = useState([]); // Available departments
  const [showDepartmentModal, setShowDepartmentModal] = useState(false); // Modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false); // Add user modal state
  
  // Add user form state
  const [newUserEmail, setNewUserEmail] = useState("");
  const [addingUser, setAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState("");
  const [addUserSuccess, setAddUserSuccess] = useState("");
  
  // Filter for active/inactive users
  const [showInactive, setShowInactive] = useState(false);

  // Fetch users directly from /workspaces/ai-apps-dashboard/users collection
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        // Fetch users directly from /workspaces/ai-apps-dashboard/users collection
        const usersRef = collection(db, "workspaces", "ai-apps-dashboard", "users");
        const usersSnap = await getDocs(usersRef);
        
        // Map the users data
        let usersData = usersSnap.docs.map((doc) => ({
          id: doc.id,
          email: doc.data().email || "",
          name: doc.data().name || "",
          fullName: doc.data().fullName || doc.data().name || "",
          displayName: doc.data().fullName || doc.data().name || "",
          department: doc.data().department || null,
          isActive: doc.data().isActive !== undefined ? doc.data().isActive : true,
          createdAt: doc.data().createdAt,
          updatedAt: doc.data().updatedAt,
        }));

        console.log("Setting users state with", usersData.length, "users from /workspaces/ai-apps-dashboard/users");
        setUsers(usersData);
        
        // Fetch departments for all users
        await fetchUserDepartments(usersData);
        
        // Fetch available departments
        const deptResult = await getDepartments();
        if (deptResult.ok) {
          setDepartments(deptResult.departments);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        // Set empty array on error to prevent showing stale data
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Fetch departments for users from employees collection
  // Uses exact email match first, then falls back to email prefix (before @)
  // so that different domains for the same person share the same department.
  // Returns the departments map for immediate use
  const fetchUserDepartments = async (usersList) => {
    try {
      const departmentsMap = {};
      
      // Fetch all employees at once
      const employeesRef = collection(db, "cynetdatabase", "root", "employees");
      const employeesSnap = await getDocs(employeesRef);
      
      // Create maps:
      // - email -> department (exact match)
      // - prefix -> department (fallback when exact email not found)
      const emailToDepartment = {};
      const prefixToDepartment = {};
      
      employeesSnap.docs.forEach((doc) => {
        const data = doc.data();
        if (data.email && data.department && typeof data.department === "string") {
          const normalizedEmail = data.email.toLowerCase().trim();
          const department = data.department.trim();
          const prefix = normalizedEmail.split("@")[0];

          if (department) {
            emailToDepartment[normalizedEmail] = department;
            // Only set prefix mapping if not already set; first one wins.
            if (!prefixToDepartment[prefix]) {
              prefixToDepartment[prefix] = department;
            }
          }
        }
      });
      
      // Map departments for each user:
      // 1) Try exact email match
      // 2) If not found, try prefix-based match
      usersList.forEach((user) => {
        if (user.email && typeof user.email === "string") {
          const normalizedEmail = user.email.toLowerCase().trim();
          const prefix = normalizedEmail.split("@")[0];
          
          const deptExact = emailToDepartment[normalizedEmail];
          const deptByPrefix = prefixToDepartment[prefix];
          
          departmentsMap[normalizedEmail] = deptExact || deptByPrefix || "—";
        }
      });
      
      setUserDepartments(departmentsMap);
      return departmentsMap; // Return the map for immediate use
    } catch (err) {
      console.error("Error fetching user departments:", err);
      return {};
    }
  };

  const handleAction = async (userId, action) => {
    setUpdatingUserId(userId);

    try {
      // Update users collection directly
      const userRef = doc(db, "workspaces", "ai-apps-dashboard", "users", userId);
      
      if (action === "inactive") {
        // Update isActive to false
        await updateDoc(userRef, {
          isActive: false,
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        const updatedUsers = users.map((u) =>
          u.id === userId ? { ...u, isActive: false } : u
        );
        setUsers(updatedUsers);
      } else if (action === "active") {
        // Update isActive to true
        await updateDoc(userRef, {
          isActive: true,
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        const updatedUsers = users.map((u) =>
          u.id === userId ? { ...u, isActive: true } : u
        );
        setUsers(updatedUsers);
      }
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setUpdatingUserId(null);
    }
  };

  // Get unique departments from userDepartments
  const uniqueDepartments = Array.from(
    new Set(Object.values(userDepartments).filter(dept => dept && dept !== "—"))
  ).sort();


  // Fetch user's department and name
  // Priority: 1) /workspaces/ai-apps-dashboard/users collection, 2) employees collection
  const fetchUserInfoFromDB = async (email) => {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const emailPrefix = normalizedEmail.split('@')[0];
      
      let department = null;
      let fullName = null;
      let name = null;
      
      // FIRST: Check /workspaces/ai-apps-dashboard/users collection (has proper full names)
      try {
        const usersRef = collection(db, "workspaces", "ai-apps-dashboard", "users");
        const usersSnap = await getDocs(usersRef);
        
        usersSnap.docs.forEach((doc) => {
          const data = doc.data();
          if (data.email) {
            const userEmail = data.email.toLowerCase().trim();
            if (userEmail === normalizedEmail) {
              
              if (data.name && typeof data.name === 'string') {
                const trimmedName = data.name.trim();
                name = trimmedName;
                fullName = trimmedName; // Use name as fullName since it's the full name
              }
              if (data.department && typeof data.department === 'string') {
                department = data.department.trim();
              }
            }
          }
        });
      } catch (usersError) {
        console.error("Error fetching from users collection:", usersError);
      }
      
      // SECOND: If name not found, check employees collection
      if (!fullName || !name) {
        const employeesRef = collection(db, "cynetdatabase", "root", "employees");
        const employeesSnap = await getDocs(employeesRef);
        
        // Try exact email match first
        employeesSnap.docs.forEach((doc) => {
          const data = doc.data();
          if (data.email) {
            const empEmail = data.email.toLowerCase().trim();
            if (empEmail === normalizedEmail) {
              if (!department && data.department && typeof data.department === 'string') {
                department = data.department.trim();
              }
              if (!fullName) {
                if (data.fullName && typeof data.fullName === 'string') {
                  fullName = data.fullName.trim();
                } else if (data.name && typeof data.name === 'string') {
                  fullName = data.name.trim();
                }
              }
              if (!name && data.name && typeof data.name === 'string') {
                name = data.name.trim();
              }
            }
          }
        });
        
        // If not found, try prefix match
        if (!department || !fullName) {
          employeesSnap.docs.forEach((doc) => {
            const data = doc.data();
            if (data.email) {
              const empEmail = data.email.toLowerCase().trim();
              const empPrefix = empEmail.split('@')[0];
              if (empPrefix === emailPrefix) {
                if (!department && data.department && typeof data.department === 'string') {
                  department = data.department.trim();
                }
                if (!fullName) {
                  if (data.fullName && typeof data.fullName === 'string') {
                    fullName = data.fullName.trim();
                  } else if (data.name && typeof data.name === 'string') {
                    fullName = data.name.trim();
                  }
                }
                if (!name && data.name && typeof data.name === 'string') {
                  name = data.name.trim();
                }
              }
            }
          });
        }
      }
      
      return { department, fullName, name };
    } catch (error) {
      console.error("Error fetching user info:", error);
      return { department: null, fullName: null, name: null };
    }
  };

  // Handle adding new user to users collection
  const handleAddNewUser = async () => {
    if (!newUserEmail || !newUserEmail.trim()) {
      setAddUserError("Please enter an email address");
      return;
    }

    const normalizedEmail = newUserEmail.toLowerCase().trim();
    
    // Check if user already exists in users collection
    try {
      const usersRef = collection(db, "workspaces", "ai-apps-dashboard", "users");
      const usersSnap = await getDocs(usersRef);
      
      const userExists = usersSnap.docs.some(doc => {
        const userData = doc.data();
        const userEmail = userData.email?.toLowerCase().trim();
        return userEmail === normalizedEmail;
      });
      
      if (userExists) {
        setAddUserError("User already exists in users collection");
        return;
      }
    } catch (error) {
      console.error("Error checking existing user:", error);
    }

    setAddingUser(true);
    setAddUserError("");
    setAddUserSuccess("");

    try {
      // Fetch department and name from employees database
      const userInfo = await fetchUserInfoFromDB(normalizedEmail);
      const { department, fullName, name } = userInfo;
      
      // Use fetched name or fallback to email prefix
      const displayName = fullName || name || normalizedEmail.split('@')[0];
      const displayNameShort = name || normalizedEmail.split('@')[0];
      
      // Add user to /workspaces/ai-apps-dashboard/users collection
      const usersRef = collection(db, "workspaces", "ai-apps-dashboard", "users");
      console.log("Adding user to users collection:", normalizedEmail);
      
      const docRef = await addDoc(usersRef, {
        email: normalizedEmail,
        name: displayNameShort,
        fullName: displayName,
        department: department || null,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log("User added successfully to users collection with ID:", docRef.id);

      // Refresh users list by fetching directly from users collection
      const refreshedUsersSnap = await getDocs(usersRef);
      let refreshedUsers = refreshedUsersSnap.docs.map((doc) => ({
        id: doc.id,
        email: doc.data().email || "",
        name: doc.data().name || "",
        fullName: doc.data().fullName || doc.data().name || "",
        displayName: doc.data().fullName || doc.data().name || "",
        department: doc.data().department || null,
        isActive: doc.data().isActive !== undefined ? doc.data().isActive : true,
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt,
      }));
      
      setUsers(refreshedUsers);
      
      // Fetch departments and wait for it to complete to get accurate department info
      const departmentsMap = await fetchUserDepartments(refreshedUsers);
      
      // Get the department from the returned map (which is populated by fetchUserDepartments)
      // This ensures we show the department that was actually found, even if it wasn't found initially
      const foundDepartment = departmentsMap[normalizedEmail] || department || null;
      const departmentDisplay = foundDepartment && foundDepartment !== "—" ? foundDepartment : "Not found";

      setAddUserSuccess(`User added successfully! Department: ${departmentDisplay}`);
      setNewUserEmail("");
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setAddUserSuccess("");
        setShowAddUserModal(false);
      }, 3000);
    } catch (error) {
      console.error("Error adding user:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        setAddUserError("Permission denied. Please check Firestore security rules.");
      } else if (error.code === 'unavailable') {
        setAddUserError("Firestore is unavailable. Please check your internet connection.");
      } else {
        setAddUserError(`Failed to add user: ${error.message || "Unknown error"}`);
      }
    } finally {
      setAddingUser(false);
    }
  };


  // Handle department-wise activation/deactivation
  // Updates isActive field in users collection
  const handleDepartmentAction = async (department, action) => {
    setUpdatingDepartment(department);

    try {
      // Find all users in this department
      const departmentUsers = users.filter((u) => {
        const userDept = userDepartments[u.email?.toLowerCase()?.trim()] || u.department || "—";
        return userDept === department;
      });

      if (departmentUsers.length === 0) {
        setUpdatingDepartment(null);
        return;
      }

      const newStatus = action === "active" ? true : false;
      
      // Update isActive for all users in this department in users collection
      const updatePromises = departmentUsers.map((u) => {
        const userRef = doc(db, "workspaces", "ai-apps-dashboard", "users", u.id);
        return updateDoc(userRef, {
          isActive: newStatus,
          updatedAt: serverTimestamp()
        });
      });

      await Promise.all(updatePromises);
      
      // Update local state
      const updatedUsers = users.map((u) => {
        const userDept = userDepartments[u.email?.toLowerCase()?.trim()] || u.department || "—";
        if (userDept === department) {
          return { ...u, isActive: newStatus };
        }
        return u;
      });
      setUsers(updatedUsers);
    } catch (error) {
      console.error("Error updating department users:", error);
    } finally {
      setUpdatingDepartment(null);
    }
  };

  // Calculate department statistics
  const getDepartmentStats = () => {
    const stats = {};
    uniqueDepartments.forEach((dept) => {
      const deptUsers = users.filter((u) => {
        const userDept = userDepartments[u.email?.toLowerCase()?.trim()] || "—";
        return userDept === dept;
      });
      const activeCount = deptUsers.filter((u) => u.isActive !== false).length;
      const inactiveCount = deptUsers.filter((u) => u.isActive === false).length;
      const allActive = deptUsers.length > 0 && activeCount === deptUsers.length;
      const allInactive = deptUsers.length > 0 && inactiveCount === deptUsers.length;
      
      stats[dept] = {
        total: deptUsers.length,
        active: activeCount,
        inactive: inactiveCount,
        allActive,
        allInactive,
        mixed: deptUsers.length > 0 && !allActive && !allInactive
      };
    });
    return stats;
  };

  const departmentStats = getDepartmentStats();

  // Filter users based on active/inactive status, search, and department
  const filteredUsers = users.filter((u) => {
    // Filter by active/inactive status
    if (showInactive) {
      // Show only inactive users
      if (u.isActive !== false) return false;
    } else {
      // Show only active users (default)
      if (u.isActive === false) return false;
    }
    
    // Filter by department
    if (selectedDepartment) {
      const userDept = userDepartments[u.email?.toLowerCase()?.trim()] || u.department || "—";
      if (userDept !== selectedDepartment) return false;
    }
    
    // Filter by search query
    const q = search.toLowerCase();
    return (
      (u.displayName && u.displayName.toLowerCase().includes(q)) ||
      (u.fullName && u.fullName.toLowerCase().includes(q)) ||
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q))
    );
  });
  
  // Debug logging
  useEffect(() => {
    console.log("Users state:", users.length, "users");
    console.log("Filtered users:", filteredUsers.length, "users");
    console.log("Show inactive:", showInactive);
    console.log("Selected department:", selectedDepartment);
    console.log("Search query:", search);
  }, [users, filteredUsers, showInactive, selectedDepartment, search]);
  
  // Get inactive users count for display
  const inactiveUsersCount = users.filter(u => u.isActive === false).length;

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when search or department filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedDepartment]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh] text-gray-500 dark:text-gray-300">
        Loading users...
      </div>
    );

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Devfolio User Access
        </h2>
        {/* Toggle Active/Inactive Users - Icon Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInactive(false)}
            className={`relative p-2.5 rounded-lg transition group ${
              !showInactive
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            title="Active Users"
          >
            <CheckCircle size={20} />
            {/* Tooltip */}
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Active Users
            </span>
          </button>
          <button
            onClick={() => setShowInactive(true)}
            className={`relative p-2.5 rounded-lg transition group ${
              showInactive
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            title="Inactive Users"
          >
            <XCircle size={20} />
            {inactiveUsersCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {inactiveUsersCount}
              </span>
            )}
            {/* Tooltip */}
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Inactive Users
            </span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-900 dark:to-indigo-800 rounded-2xl shadow-md flex items-center justify-between"
      >
        <div>
          <h2 className="text-sm font-medium text-gray-600 dark:text-gray-300">
            Total Users
          </h2>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {showInactive ? inactiveUsersCount : users.filter(u => u.isActive !== false).length}
          </p>
          <p className="text-sm text-indigo-600 mt-1">{showInactive ? 'Inactive Members' : 'Active Members'}</p>
        </div>
        <Users className="text-indigo-500" size={38} />
      </motion.div>

      {/* Department Modal */}
      <AnimatePresence>
        {showDepartmentModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDepartmentModal(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                    Department Access Control
                  </h3>
                  <button
                    onClick={() => setShowDepartmentModal(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  >
                    <X size={20} className="text-gray-600 dark:text-gray-400" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {uniqueDepartments.map((dept) => {
                      const stats = departmentStats[dept];
                      const isUpdating = updatingDepartment === dept;
                      const isAllActive = stats?.allActive;
                      const isAllInactive = stats?.allInactive;
                      
                      return (
                        <div
                          key={dept}
                          className={`p-4 border rounded-lg transition ${
                            isUpdating
                              ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 opacity-75'
                              : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600'
                          }`}
                        >
                          <div className="flex flex-col gap-3">
                            <div>
                              <h4 className="font-medium text-gray-800 dark:text-gray-100">{dept}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {stats?.active || 0} Active / {stats?.total || 0} Total
                              </p>
                            </div>
                            <div className="flex flex-col gap-2">
                              {isUpdating ? (
                                <span className="text-xs text-blue-600 dark:text-blue-400 text-center py-2">Updating...</span>
                              ) : (
                                <>
                                  {/* User Status Controls */}
                                  <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">User Status:</p>
                                    <button
                                      onClick={() => handleDepartmentAction(dept, "active")}
                                      disabled={isAllActive || isUpdating}
                                      className={`w-full px-3 py-1.5 text-xs font-medium rounded-lg transition mb-1 ${
                                        isAllActive
                                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 cursor-not-allowed'
                                          : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50'
                                      }`}
                                    >
                                      Activate All
                                    </button>
                                    <button
                                      onClick={() => handleDepartmentAction(dept, "inactive")}
                                      disabled={isAllInactive || isUpdating}
                                      className={`w-full px-3 py-1.5 text-xs font-medium rounded-lg transition ${
                                        isAllInactive
                                          ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                                          : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50'
                                      }`}
                                    >
                                      Deactivate All
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                  <button
                    onClick={() => setShowDepartmentModal(false)}
                    className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Search Bar and Department Filter */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-3 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        
        {/* Department Filter */}
        <div className="min-w-[200px]">
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
            focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:bg-gray-800 dark:text-gray-100
            bg-white text-gray-900 dark:text-gray-100 cursor-pointer"
          >
            <option value="">All Departments</option>
            {uniqueDepartments.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>

        {/* Department Access Control Button */}
        {uniqueDepartments.length > 0 && (
          <button
            onClick={() => setShowDepartmentModal(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md whitespace-nowrap"
          >
            <Building2 size={18} />
            Department Access
          </button>
        )}

        {/* Add New User Button */}
        <button
          onClick={() => {
            setShowAddUserModal(true);
            setNewUserEmail("");
            setAddUserError("");
            setAddUserSuccess("");
          }}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md whitespace-nowrap"
        >
          <Plus size={18} />
          Add New User
        </button>
      </div>

      {/* Add User Modal */}
      <AnimatePresence>
        {showAddUserModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddUserModal(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                    Add New User
                  </h3>
                  <button
                    onClick={() => {
                      setShowAddUserModal(false);
                      setNewUserEmail("");
                      setAddUserError("");
                      setAddUserSuccess("");
                    }}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  >
                    <X size={20} className="text-gray-600 dark:text-gray-400" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Enter the user's email address. The system will automatically fetch their department from the database and add them to the users collection.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => {
                          setNewUserEmail(e.target.value);
                          setAddUserError("");
                        }}
                        placeholder="user@example.com"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                        focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:bg-gray-800 dark:text-gray-100"
                        disabled={addingUser}
                      />
                    </div>

                    {addUserError && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-700 dark:text-red-300">{addUserError}</p>
                      </div>
                    )}

                    {addUserSuccess && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <p className="text-sm text-green-700 dark:text-green-300">{addUserSuccess}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowAddUserModal(false);
                      setNewUserEmail("");
                      setAddUserError("");
                      setAddUserSuccess("");
                    }}
                    disabled={addingUser}
                    className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddNewUser}
                    disabled={addingUser || !newUserEmail.trim()}
                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {addingUser ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        Add User
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Users Table */}
      <div className="mt-4 bg-white dark:bg-gray-900 rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          {showInactive ? 'Inactive Users' : 'Active Users'}
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Department</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {currentUsers.map((u, index) => (
                <tr
                  key={`user-${u.id || u.email || index}`}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition ${
                    updatingUserId === u.id ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-100">
                    {u.displayName || u.fullName || u.name || "Unnamed"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {u.email || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {userDepartments[u.email?.toLowerCase()?.trim()] || u.department || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {updatingUserId === u.id ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Updating...
                      </span>
                    ) : (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          u.isActive
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {u.isActive ? "Active" : "Inactive"}
                      </span>
                    )}
                  </td>

                  {/* Toggle Switch */}
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleAction(u.id, u.isActive ? "inactive" : "active")}
                      disabled={updatingUserId === u.id}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 ${
                        u.isActive
                          ? "bg-indigo-600"
                          : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          u.isActive ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </td>
                </tr>
              ))}

              {filteredUsers.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                  >
                    No matching users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {filteredUsers.length > 0 && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
            </div>
            
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border ${
                  currentPage === 1
                    ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <ChevronLeft size={18} />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-3 py-1.5 text-gray-500 dark:text-gray-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        currentPage === page
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border ${
                  currentPage === totalPages
                    ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
